/**
 * Deployment Socket.IO Client
 *
 * Client-side Socket.IO connection for real-time deployment synchronization.
 */

import { io, Socket } from 'socket.io-client';
import { getAuth } from 'firebase/auth';
import type {
  DeploymentClientEvents,
  DeploymentServerEvents,
  CreaturePlacement,
  MatchDeploymentStatus,
  SeriesRecord
} from '@drawn-of-war/shared';
import { getApiUrl } from '../config/api';

/**
 * Deployment socket client
 */
export class DeploymentSocketClient {
  private socket: Socket<DeploymentServerEvents, DeploymentClientEvents> | null = null;
  private matchId: string | null = null;
  private playerId: 'player1' | 'player2' | null = null;
  private joinPromise: Promise<{
    player1Placements: CreaturePlacement[];
    player2Placements: CreaturePlacement[];
    deploymentStatus: MatchDeploymentStatus;
    creatureLimit: number;
    player1DisplayName?: string;
    player2DisplayName?: string;
  } | null> | null = null;

  /**
   * Ensure socket is connected (idempotent)
   * @param authToken - Firebase ID token for authentication
   */
  private ensureConnection(authToken?: string): void {
    // If socket exists and is connected, nothing to do
    if (this.socket?.connected) {
      return;
    }

    // If socket exists but disconnected, clean it up first
    if (this.socket) {
      console.log('[Deployment Socket] Existing socket disconnected, cleaning up...');
      // Full cleanup is OK here since we're destroying the socket entirely
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.joinPromise = null; // Reset join promise for new connection
    }

    const socketUrl = `${getApiUrl()}/deployment`;
    console.log('[Deployment Socket] Creating socket connection');
    console.log('[Deployment Socket] Socket URL:', socketUrl);
    console.log('[Deployment Socket] Auth token provided:', !!authToken);

    // Create socket with autoConnect: false to ensure handlers are set up before connection starts
    this.socket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      autoConnect: false,  // IMPORTANT: Don't auto-connect, we'll connect after setting up handlers
      auth: {
        token: authToken || ''  // Pass Firebase ID token for authentication
      }
    });

    // Set up ALL event handlers BEFORE connecting
    this.socket.on('connect', () => {
      console.log('[Deployment Socket] ✅ CONNECTED! Socket ID:', this.socket?.id);
      console.log('[Deployment Socket] Transport:', this.socket?.io.engine?.transport?.name);
      // Auto-join match on reconnect
      if (this.matchId && this.playerId) {
        this.joinMatch(this.matchId, this.playerId);
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[Deployment Socket] ❌ DISCONNECTED. Reason:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('[Deployment Socket] ❌ CONNECTION ERROR:', error);
      console.error('[Deployment Socket] Error message:', error.message);
      console.error('[Deployment Socket] Error name:', error.name);
    });

    this.socket.io.on('reconnect_attempt', () => {
      console.log('[Deployment Socket] 🔄 Attempting reconnect...');
    });

    this.socket.io.on('reconnect_failed', () => {
      console.error('[Deployment Socket] ❌ Reconnect failed');
    });

    // NOW connect after all handlers are set up
    console.log('[Deployment Socket] Handlers set up, initiating connection...');
    this.socket.connect();

    console.log('[Deployment Socket] Socket instance created, ID:', this.socket.id);
    console.log('[Deployment Socket] Socket connected:', this.socket.connected);
    console.log('[Deployment Socket] Socket transport:', this.socket.io.engine?.transport?.name);
    console.log('[Deployment Socket] Socket created, waiting for connection...');
  }

  /**
   * Connect to deployment socket and join match
   * Returns a promise that resolves with the initial state when joined
   */
  async connect(matchId: string, playerId: 'player1' | 'player2'): Promise<{
    player1Placements: CreaturePlacement[];
    player2Placements: CreaturePlacement[];
    deploymentStatus: MatchDeploymentStatus;
    creatureLimit: number;
    player1DisplayName?: string;
    player2DisplayName?: string;
  } | null> {
    this.matchId = matchId;
    this.playerId = playerId;

    // If already joining, wait for that promise
    if (this.joinPromise) {
      return this.joinPromise;
    }

    // Get Firebase auth token for socket authentication
    let authToken: string | undefined;
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        authToken = await user.getIdToken();
        console.log('[Deployment Socket] Got Firebase ID token for user:', user.uid);
      } else {
        console.warn('[Deployment Socket] No authenticated user found');
      }
    } catch (error) {
      console.error('[Deployment Socket] Failed to get auth token:', error);
    }

    // Create join promise that returns initial state
    this.joinPromise = new Promise<{
      player1Placements: CreaturePlacement[];
      player2Placements: CreaturePlacement[];
      deploymentStatus: MatchDeploymentStatus;
      creatureLimit: number;
      player1DisplayName?: string;
      player2DisplayName?: string;
    } | null>((resolve, reject) => {
      // Ensure we have a socket connection (creates if needed)
      this.ensureConnection(authToken);

      if (!this.socket) {
        reject(new Error('Failed to create socket'));
        return;
      }

      // Listen for successful join (state received means join succeeded)
      // IMPORTANT: Capture the state data and return it so callers can use it
      const onState = (data: {
        matchId: string;
        player1Placements: CreaturePlacement[];
        player2Placements: CreaturePlacement[];
        deploymentStatus: MatchDeploymentStatus;
        creatureLimit: number;
        player1DisplayName?: string;
        player2DisplayName?: string;
      }) => {
        this.socket?.off('deployment:state', onState);
        this.socket?.off('deployment:error', onError);
        console.log('[Deployment Socket] Successfully joined match, returning initial state');
        console.log('[Deployment Socket] Player names:', data.player1DisplayName, data.player2DisplayName);
        // Return the state data so callers can use it immediately
        resolve({
          player1Placements: data.player1Placements,
          player2Placements: data.player2Placements,
          deploymentStatus: data.deploymentStatus,
          creatureLimit: data.creatureLimit,
          player1DisplayName: data.player1DisplayName,
          player2DisplayName: data.player2DisplayName
        });
      };

      const onError = (data: { message: string }) => {
        this.socket?.off('deployment:state', onState);
        this.socket?.off('deployment:error', onError);
        console.error('[Deployment Socket] Join failed:', data.message);
        reject(new Error(data.message));
      };

      // Set up listeners
      this.socket.once('deployment:state', onState);
      this.socket.once('deployment:error', onError);

      // Join immediately if already connected
      if (this.socket.connected) {
        this.joinMatch(matchId, playerId);
      }
      // Otherwise wait for connect event (which will auto-join via lines 44-49)
    });

    return this.joinPromise;
  }

  /**
   * Disconnect from deployment socket
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.matchId = null;
      this.playerId = null;
      this.joinPromise = null;
      console.log('[Deployment Socket] Disconnected');
    }
  }

  /**
   * Join a match room
   */
  joinMatch(matchId: string, playerId: 'player1' | 'player2'): void {
    if (!this.socket) {
      console.error('[Deployment Socket] Not connected');
      return;
    }

    this.socket.emit('deployment:join', { matchId, playerId });
    console.log(`[Deployment Socket] Joining match ${matchId} as ${playerId}`);
  }

  /**
   * Place a creature
   */
  placeCreature(placement: CreaturePlacement): void {
    if (!this.socket || !this.matchId || !this.playerId) {
      console.error('[Deployment Socket] Not connected or not joined');
      return;
    }

    this.socket.emit('deployment:place', {
      matchId: this.matchId,
      playerId: this.playerId,
      placement
    });
  }

  /**
   * Remove a creature
   */
  removeCreature(creatureId: string): void {
    if (!this.socket || !this.matchId || !this.playerId) {
      console.error('[Deployment Socket] Not connected or not joined');
      return;
    }

    this.socket.emit('deployment:remove', {
      matchId: this.matchId,
      playerId: this.playerId,
      creatureId
    });
  }

  /**
   * Update all placements (bulk update)
   */
  updatePlacements(placements: CreaturePlacement[]): void {
    if (!this.socket || !this.matchId || !this.playerId) {
      console.error('[Deployment Socket] Not connected or not joined');
      return;
    }

    this.socket.emit('deployment:update-placements', {
      matchId: this.matchId,
      playerId: this.playerId,
      placements
    });
  }

  /**
   * Mark player as ready
   */
  markReady(): void {
    if (!this.socket || !this.matchId || !this.playerId) {
      console.error('[Deployment Socket] Not connected or not joined');
      return;
    }

    this.socket.emit('deployment:ready', {
      matchId: this.matchId,
      playerId: this.playerId
    });
  }

  /**
   * Unmark player as ready
   */
  markUnready(): void {
    if (!this.socket || !this.matchId || !this.playerId) {
      console.error('[Deployment Socket] Not connected or not joined');
      return;
    }

    this.socket.emit('deployment:unready', {
      matchId: this.matchId,
      playerId: this.playerId
    });
  }

  /**
   * Request a rematch (Fight Again)
   * Resets the match to deployment phase for another round
   */
  requestRematch(): void {
    if (!this.socket || !this.matchId || !this.playerId) {
      console.error('[Deployment Socket] Not connected or not joined');
      return;
    }

    console.log('[Deployment Socket] Requesting rematch');
    this.socket.emit('deployment:request-rematch', {
      matchId: this.matchId,
      playerId: this.playerId
    });
  }

  /**
   * Leave the match series entirely
   * Disconnects from the match and returns to lobby
   */
  leaveMatch(): void {
    if (!this.socket || !this.matchId || !this.playerId) {
      console.error('[Deployment Socket] Not connected or not joined');
      return;
    }

    console.log('[Deployment Socket] Leaving match');
    this.socket.emit('deployment:leave-match', {
      matchId: this.matchId,
      playerId: this.playerId
    });
  }

  /**
   * Listen for initial state
   */
  onState(
    callback: (data: {
      matchId: string;
      player1Placements: CreaturePlacement[];
      player2Placements: CreaturePlacement[];
      deploymentStatus: MatchDeploymentStatus;
      creatureLimit: number;
      player1DisplayName?: string;
      player2DisplayName?: string;
    }) => void
  ): void {
    this.socket?.on('deployment:state', callback);
  }

  /**
   * Listen for opponent placing a creature
   */
  onOpponentPlaced(
    callback: (data: { playerId: 'player1' | 'player2'; placement: CreaturePlacement }) => void
  ): void {
    this.socket?.on('deployment:opponent-placed', callback);
  }

  /**
   * Listen for opponent removing a creature
   */
  onOpponentRemoved(
    callback: (data: { playerId: 'player1' | 'player2'; creatureId: string }) => void
  ): void {
    this.socket?.on('deployment:opponent-removed', callback);
  }

  /**
   * Listen for opponent bulk update
   */
  onOpponentUpdated(
    callback: (data: { playerId: 'player1' | 'player2'; placements: CreaturePlacement[] }) => void
  ): void {
    this.socket?.on('deployment:opponent-updated', callback);
  }

  /**
   * Listen for deployment status changes
   */
  onStatusChanged(callback: (status: MatchDeploymentStatus) => void): void {
    this.socket?.on('deployment:status-changed', callback);
  }

  /**
   * Listen for errors
   */
  onError(callback: (data: { message: string; code?: string }) => void): void {
    this.socket?.on('deployment:error', callback);
  }

  /**
   * Listen for opponent connection
   */
  onOpponentConnected(callback: (data: { playerId: 'player1' | 'player2' }) => void): void {
    this.socket?.on('deployment:opponent-connected', callback);
  }

  /**
   * Listen for opponent disconnection
   */
  onOpponentDisconnected(callback: (data: { playerId: 'player1' | 'player2' }) => void): void {
    this.socket?.on('deployment:opponent-disconnected', callback);
  }

  /**
   * Listen for combat started
   */
  onCombatStarted(callback: (data: { matchId: string }) => void): void {
    this.socket?.on('deployment:combat-started', callback);
  }

  /**
   * Listen for combat completed
   */
  onCombatCompleted(callback: (data: {
    matchId: string;
    winner: 'player1' | 'player2' | 'draw';
    reason: string;
    duration: number;
    seriesRecord?: SeriesRecord;
    roundNumber?: number;
  }) => void): void {
    this.socket?.on('deployment:combat-completed', callback);
  }

  /**
   * Listen for round reset (Fight Again)
   * Triggered when match is reset to deployment for another round
   */
  onRoundReset(callback: (data: {
    matchId: string;
    roundNumber: number;
    seriesRecord: SeriesRecord;
    deploymentStatus: MatchDeploymentStatus;
    creatureLimit: number;
  }) => void): void {
    this.socket?.on('deployment:round-reset', callback);
  }

  /**
   * Listen for opponent leaving the match series
   */
  onOpponentLeft(callback: (data: { playerId: 'player1' | 'player2' }) => void): void {
    this.socket?.on('deployment:opponent-left', callback);
  }

  /**
   * Remove all application-level event listeners.
   * Uses specific off() calls to avoid removing internal Socket.IO handlers
   * (connect, disconnect, connect_error) which are needed for reconnection.
   */
  removeAllListeners(): void {
    if (!this.socket) return;
    this.socket.off('deployment:state');
    this.socket.off('deployment:opponent-placed');
    this.socket.off('deployment:opponent-removed');
    this.socket.off('deployment:opponent-updated');
    this.socket.off('deployment:status-changed');
    this.socket.off('deployment:error');
    this.socket.off('deployment:opponent-connected');
    this.socket.off('deployment:opponent-disconnected');
    this.socket.off('deployment:combat-started');
    this.socket.off('deployment:combat-completed');
    this.socket.off('deployment:round-reset');
    this.socket.off('deployment:opponent-left');
  }

  /**
   * Check if connected
   */
  get isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get current match ID
   */
  get currentMatchId(): string | null {
    return this.matchId;
  }

  /**
   * Get current player ID
   */
  get currentPlayerId(): 'player1' | 'player2' | null {
    return this.playerId;
  }
}

/**
 * Singleton instance for easy access
 */
export const deploymentSocket = new DeploymentSocketClient();
